import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, X, ChevronDown, ChevronUp } from "lucide-react";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { useEntityQuickAdd } from '@/components/shared/useEntityQuickAdd';
import { NumericInput } from "@/components/shared/NumericInput";
import { cn } from "@/lib/utils";
import { type ProductComponentData } from "../../../../shared/schemas/product.schema";
import { ProductProcessConfigsList } from "./ProductProcessConfigsList";
import { PaperSizeInput } from "@/components/shared/PaperSizeInput";

interface ProductComponentsListProps {
  components: ProductComponentData[];
  onChange: (components: ProductComponentData[]) => void;
  materialLookup: any;
  processLookup: any;
}

export function ProductComponentsList({
  components,
  onChange,
  materialLookup,
  processLookup,
}: ProductComponentsListProps) {
  const processQuickAdd = useEntityQuickAdd("process"); // thêm mới bản ghi cha theo quyền (03/11/2026)
  const materialQuickAdd = useEntityQuickAdd("material"); // thêm mới bản ghi cha theo quyền (03/11/2026)
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const addComponent = () => {
    const id = Math.random().toString(36).substring(7);
    onChange([
      ...components,
      {
        id, // temp id for UI
        componentName: "",
        quantity: 1,
        unit: "cái",
        materialIds: [],
        processIds: [],
        processConfigs: [],
      } as unknown as ProductComponentData,
    ]);
    setExpandedIds((prev) => [...prev, id]);
  };

  const updateComponent = (
    index: number,
    updates: Partial<ProductComponentData>,
  ) => {
    const next = [...components];
    next[index] = { ...next[index], ...updates };
    onChange(next);
  };

  const removeComponent = (index: number) => {
    const next = [...components];
    next.splice(index, 1);
    onChange(next);
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  if (components.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg bg-slate-50 border-slate-200">
        <p className="text-sm text-slate-500 mb-4">
          Sản phẩm này là Combo/Bộ, hãy thêm các thành phần con
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={addComponent}
          className="gap-2"
        >
          <Plus className="w-4 h-4" /> Thêm thành phần
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {components.map((comp, index) => {
        const uiId = comp.id || `comp-${index}`;
        const isExpanded = expandedIds.includes(uiId);

        return (
          <div
            key={uiId}
            className="border rounded-lg bg-white overflow-hidden shadow-sm"
          >
            {/* Header */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 border-b">
              <button
                type="button"
                onClick={() => toggleExpand(uiId)}
                className="p-1 hover:bg-slate-200 rounded"
              >
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
              <div className="flex-1 font-medium text-sm">
                {comp.componentName || `Thành phần #${index + 1}`}
              </div>
              <div className="flex items-center gap-4">
                <div className="text-xs text-slate-500">
                  SL: {comp.quantity} {comp.unit}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => removeComponent(index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Body */}
            {isExpanded && (
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">
                      Tên thành phần <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={comp.componentName}
                      onChange={(e) =>
                        updateComponent(index, {
                          componentName: e.target.value,
                        })
                      }
                      className="h-9 text-sm"
                      placeholder="VD: Hộp, Nắp, Khay..."
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">
                      Số lượng / Bộ <span className="text-red-500">*</span>
                    </Label>
                    <NumericInput
                      value={comp.quantity}
                      onChange={(val) =>
                        updateComponent(index, { quantity: val || 1 })
                      }
                      placeholder="1"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Đơn vị</Label>
                    <Input
                      value={comp.unit || ""}
                      onChange={(e) =>
                        updateComponent(index, { unit: e.target.value })
                      }
                      className="h-9 text-sm"
                      placeholder="cái, bộ..."
                    />
                  </div>
                </div>

                {/* Dimensions */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">DxRxC (mm)</Label>
                    <div className="flex gap-1">
                      <NumericInput
                        value={comp.length ?? ""}
                        onChange={(val) => updateComponent(index, { length: val })}
                        placeholder="D"
                        className="h-9 text-sm"
                      />
                      <NumericInput
                        value={comp.width ?? ""}
                        onChange={(val) => updateComponent(index, { width: val })}
                        placeholder="R"
                        className="h-9 text-sm"
                      />
                      <NumericInput
                        value={comp.height ?? ""}
                        onChange={(val) => updateComponent(index, { height: val })}
                        placeholder="C"
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Khổ trải (DxR mm)</Label>
                    <div className="flex gap-1">
                      <NumericInput
                        value={comp.flatLength ?? ""}
                        onChange={(val) => updateComponent(index, { flatLength: val })}
                        placeholder="Dài"
                        className="h-9 text-sm"
                      />
                      <NumericInput
                        value={comp.flatWidth ?? ""}
                        onChange={(val) => updateComponent(index, { flatWidth: val })}
                        placeholder="Rộng"
                        className="h-9 text-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Khổ giấy in</Label>
                    <PaperSizeInput
                      value={comp.printFormat}
                      onChange={(v) => updateComponent(index, { printFormat: v })}
                      placeholderW="710"
                      placeholderH="450"
                      testId={`paper-size-component-${index}-printFormat`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Khổ giấy mua</Label>
                    <PaperSizeInput
                      value={comp.cutFormat}
                      onChange={(v) => updateComponent(index, { cutFormat: v })}
                      placeholderW="770"
                      placeholderH="1000"
                      testId={`paper-size-component-${index}-cutFormat`}
                    />
                  </div>
                </div>

                {/* Vật tư */}
                <div className="space-y-1">
                  <Label className="text-xs">Vật tư</Label>
                  <div className="min-h-[36px] w-full rounded-md border bg-white p-1 flex items-center flex-wrap gap-1">
                    {(comp.materialIds || []).map((mid) => {
                      const opt = materialLookup.options.find(
                        (o: any) => o.id === mid,
                      );
                      return (
                        <Badge
                          key={mid}
                          variant="secondary"
                          className="text-[11px] h-6 px-2 py-0 gap-1 bg-emerald-50 text-emerald-700"
                        >
                          {opt ? opt.label : mid}
                          <button
                            type="button"
                            className="ml-1 hover:bg-emerald-200 rounded-full p-0.5"
                            onClick={() => {
                              updateComponent(index, {
                                materialIds: comp.materialIds!.filter(
                                  (x) => x !== mid,
                                ),
                              });
                            }}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                    <div className="flex-1 min-w-[150px]">
                      <ComboboxFK {...materialQuickAdd}
                        value=""
                        onChange={(id) => {
                          if (id && !(comp.materialIds || []).includes(id)) {
                            updateComponent(index, {
                              materialIds: [...(comp.materialIds || []), id],
                            });
                          }
                        }}
                        options={materialLookup.options.filter(
                          (o: any) => !(comp.materialIds || []).includes(o.id),
                        )}
                        loading={materialLookup.isLoading}
                        label=""
                        hideLabel
                        placeholder={
                          (comp.materialIds || []).length === 0
                            ? "Chọn VT..."
                            : "+ Thêm VT"
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Công đoạn */}
                <div className="space-y-1">
                  <Label className="text-xs">Công đoạn</Label>
                  <div className="min-h-[36px] w-full rounded-md border bg-white p-1 flex items-center flex-wrap gap-1">
                    {(comp.processIds || []).map((pid) => {
                      const opt = processLookup.options.find(
                        (o: any) => o.id === pid,
                      );
                      return (
                        <Badge
                          key={pid}
                          variant="secondary"
                          className="text-[11px] h-6 px-2 py-0 gap-1 bg-blue-50 text-blue-700"
                        >
                          {opt ? opt.label : pid}
                          <button
                            type="button"
                            className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                            onClick={() => {
                              updateComponent(index, {
                                processIds: comp.processIds!.filter(
                                  (x) => x !== pid,
                                ),
                                processConfigs: (
                                  comp.processConfigs || []
                                ).filter((c) => c.processId !== pid),
                              });
                            }}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                    <div className="flex-1 min-w-[150px]">
                      <ComboboxFK {...processQuickAdd}
                        value=""
                        onChange={(id) => {
                          if (id && !(comp.processIds || []).includes(id)) {
                            const proc = processLookup.options.find(
                              (o: any) => o.id === id,
                            )?.original;
                            updateComponent(index, {
                              processIds: [...(comp.processIds || []), id],
                              processConfigs: [
                                ...(comp.processConfigs || []),
                                {
                                  processId: id,
                                  setupTimeMinutes: 0,
                                  speedPerHour: 0,
                                  runWastePercent: 0,
                                  setupWasteQty: 0,
                                  isOutsourced: false,
                                  unitCost: proc?.unitCost
                                    ? Number(proc.unitCost)
                                    : 0,
                                  batchCost: 0,
                                },
                              ],
                            });
                          }
                        }}
                        options={processLookup.options.filter(
                          (o: any) => !(comp.processIds || []).includes(o.id),
                        )}
                        loading={processLookup.isLoading}
                        label=""
                        hideLabel
                        placeholder={
                          (comp.processIds || []).length === 0
                            ? "Chọn CĐ..."
                            : "+ Thêm CĐ"
                        }
                      />
                    </div>
                  </div>
                </div>

                <ProductProcessConfigsList
                  configs={comp.processConfigs || []}
                  processLookup={processLookup}
                  onChange={(configs) =>
                    updateComponent(index, { processConfigs: configs })
                  }
                />

                <div className="space-y-1">
                  <Label className="text-xs">Kích thước & Mô tả</Label>
                  <Input
                    value={comp.specs || ""}
                    onChange={(e) =>
                      updateComponent(index, { specs: e.target.value })
                    }
                    className="h-9 text-sm"
                    placeholder="Ghi chú quy cách con"
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        onClick={addComponent}
        className="w-full gap-2 border-dashed"
      >
        <Plus className="w-4 h-4" /> Thêm thành phần khác
      </Button>
    </div>
  );
}
